import type { Cart, Price } from '@/shared/types';

/**
 * Правила корзины для оптимистичного кэша: применяются к данным до ответа
 * сервера, поэтому повторяют его формулы. Функции меняют переданный объект
 * на месте — их зовут из immer-рецептов RTK Query.
 */

/** Формат сумм как на бэке: PriceFactory в gateway и PriceDTO в каталоге */
export function rublePrice(amount: number): Price {
  const value = +amount.toFixed(2);

  return { amount: value, currency: 'RUB', name: `${value} ₽` };
}

/** Итоги корзины после правки позиций */
export function recalcTotals(cart: Cart): void {
  cart.price = rublePrice(
    cart.positions.reduce((sum, { price }) => sum + price.amount, 0)
  );

  if (!cart.availability) return;

  cart.availability = {
    ...cart.availability,
    required: cart.positions.reduce((sum, { amount }) => sum + amount, 0),
    available: cart.positions.reduce(
      (sum, { availability }) => sum + (availability.available ?? 0),
      0
    ),
  };
}

/**
 * Новое количество позиции: available = min(stock, amount), сумма — по цене
 * за штуку. Партии с разной ценой дают небольшую погрешность, её снимет ответ.
 */
export function applyAmount(
  cart: Cart,
  positionId: string,
  amount: number
): void {
  const position = cart.positions.find(({ _id }) => _id === positionId);
  if (!position || position.amount === amount) return;

  const unitPrice = position.amount
    ? position.price.amount / position.amount
    : 0;
  const { stock } = position.availability;

  position.amount = amount;
  position.price = rublePrice(unitPrice * amount);
  position.availability = {
    ...position.availability,
    required: amount,
    // Без stock (старый бэк) доступность честно посчитать нечем
    available:
      stock === undefined
        ? position.availability.available
        : Math.min(stock, amount),
  };

  recalcTotals(cart);
}

/** Позиция уходит из корзины */
export function removePosition(cart: Cart, positionId: string): void {
  const index = cart.positions.findIndex(({ _id }) => _id === positionId);
  if (index === -1) return;

  cart.positions.splice(index, 1);
  recalcTotals(cart);
}

/** Место корзины в списке «Мои корзины»: пустых корзин там нет */
export function syncInList(carts: Cart[], cart: Cart): void {
  const index = carts.findIndex(
    ({ pharmacyId }) => pharmacyId === cart.pharmacyId
  );

  if (cart.positions.length === 0) {
    if (index !== -1) carts.splice(index, 1);
    return;
  }

  if (index === -1) carts.push(cart);
  else carts[index] = cart;
}
